import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Smartphone, Truck, MapPin, Gauge, RefreshCw, Fuel, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DualLocationCard — shows PHONE GPS and ELD GPS side by side for a
 * single truck. Both sources are always displayed independently; the
 * system never overwrites one with the other.
 *
 * Sources:
 *   📱 Phone GPS = the assigned driver's latest heartbeat position
 *   🚛 ELD GPS   = the truck's latest telemetry from its linked ELD provider
 *
 * The "primary" pill is advisory (whichever timestamp is fresher wins);
 * clients that need one location for a map render can consult it, but
 * both raw source records remain intact for audit + comparison.
 *
 * Props: truckId (required)
 */
const _fmtRelative = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const _mapsUrl = (lat, lng) =>
  `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}`;

const SourceCard = ({
  testid, badge, icon: Icon, iconColor, sourceLabel, sourceTag, isPrimary,
  isUnavailable, unavailableText, position, extraRows,
}) => (
  <div
    data-testid={testid}
    className={`rounded-md border p-4 ${
      isPrimary ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-slate-200 bg-white'
    }`}
  >
    <div className="flex items-start justify-between gap-2 mb-3">
      <div className="flex items-center gap-2 min-w-0">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${iconColor}`}>
          <Icon className="w-4 h-4 text-white" strokeWidth={2.25} />
        </div>
        <div className="min-w-0">
          <div
            className="text-sm font-bold text-slate-900"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
          >
            {badge} {sourceLabel}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            {sourceTag}
          </div>
        </div>
      </div>
      {isPrimary && (
        <span
          data-testid={`${testid}-primary-pill`}
          className="text-[9px] uppercase tracking-widest font-bold text-white bg-[#002FA7] px-1.5 py-0.5 rounded"
        >
          Primary
        </span>
      )}
    </div>
    {isUnavailable ? (
      <div className="text-xs text-slate-500 py-2">
        {unavailableText}
      </div>
    ) : (
      <>
        <div className="flex items-start gap-2">
          <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
          <a
            href={_mapsUrl(position.lat, position.lng)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-mono text-slate-900 hover:text-[#002FA7] break-all"
          >
            {position.lat.toFixed(5)}, {position.lng.toFixed(5)}
          </a>
        </div>
        <div className="text-[11px] text-slate-500 mt-1.5">
          Captured {_fmtRelative(position.captured_at)}
        </div>
        {extraRows}
      </>
    )}
  </div>
);

const DualLocationCard = ({ truckId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/eld/dual-location/${truckId}`, { withCredentials: true });
      setData(res.data);
      setError(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Unable to load locations';
      setError(typeof msg === 'string' ? msg : 'Unable to load locations');
    } finally {
      setLoading(false);
    }
  }, [truckId]);

  useEffect(() => {
    if (!truckId) return undefined;
    fetchData();
    const id = setInterval(fetchData, 60_000);
    return () => clearInterval(id);
  }, [truckId, fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Kick the poller so ELD data is guaranteed fresh, then re-read.
      // Refresh is admin-only on the backend; failure is non-fatal here —
      // we still re-fetch so any cached data still updates.
      await axios.post(`${API}/eld/refresh`, {}, { withCredentials: true }).catch(() => {});
      await fetchData();
    } finally {
      setRefreshing(false);
    }
  };

  if (!truckId) return null;
  if (loading) {
    return (
      <div
        data-testid="dual-location-card"
        className="bg-white border border-slate-200 rounded-md p-6"
      >
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading locations…
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div
        data-testid="dual-location-card"
        className="bg-white border border-slate-200 rounded-md p-6"
      >
        <div className="text-xs text-slate-500">
          Locations unavailable ({error || 'no data'}).
        </div>
      </div>
    );
  }

  const { phone, eld, primary, driver_name: driverName, truck_label: truckLabel } = data;
  const nothingLinked = !phone && !eld;

  return (
    <div
      data-testid="dual-location-card"
      className="bg-white border border-slate-200 rounded-md p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3
            className="text-base font-bold tracking-tight text-slate-900"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
          >
            Live Locations
          </h3>
          <p
            className="text-xs text-slate-500 mt-0.5"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {truckLabel ? `${truckLabel}` : 'Truck'}
            {driverName ? ` · Driver: ${driverName}` : ' · No driver assigned'}
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          aria-label="Refresh"
          data-testid="dual-location-refresh"
          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {nothingLinked ? (
        <div
          data-testid="dual-location-empty"
          className="text-center py-6 text-xs text-slate-500 border border-dashed border-slate-200 rounded-md"
        >
          No location sources yet. Assign a driver (for phone GPS) or link an ELD device to see live positions.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SourceCard
            testid="dual-location-phone"
            badge="📱"
            icon={Smartphone}
            iconColor="bg-[#34C759]"
            sourceLabel="Phone GPS"
            sourceTag={driverName ? `From ${driverName}'s phone` : "Driver's phone"}
            isPrimary={primary === 'phone'}
            isUnavailable={!phone}
            unavailableText={
              driverName
                ? `${driverName} hasn't shared a GPS fix yet (permission denied or no signal).`
                : 'No driver assigned — assign one to see phone GPS.'
            }
            position={phone && { lat: phone.lat, lng: phone.lng, captured_at: phone.captured_at }}
            extraRows={phone && (
              <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-3">
                {typeof phone.accuracy_m === 'number' && (
                  <span>±{Math.round(phone.accuracy_m)}m</span>
                )}
                <span
                  className={`inline-flex items-center gap-1 ${
                    phone.online ? 'text-emerald-600' : 'text-slate-400'
                  }`}
                >
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${phone.online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  {phone.online ? 'Online' : 'Offline'}
                </span>
              </div>
            )}
          />

          <SourceCard
            testid="dual-location-eld"
            badge="🚛"
            icon={Truck}
            iconColor="bg-[#002FA7]"
            sourceLabel="ELD GPS"
            sourceTag={eld ? `From ${eld.provider || 'ELD'} device` : 'No ELD linked'}
            isPrimary={primary === 'eld'}
            isUnavailable={!eld}
            unavailableText="No ELD device linked to this truck yet."
            position={eld && { lat: eld.lat, lng: eld.lng, captured_at: eld.captured_at }}
            extraRows={eld && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600 mt-2 pt-2 border-t border-slate-100">
                {typeof eld.speed_mph === 'number' && (
                  <div className="flex items-center gap-1">
                    <Gauge className="w-3 h-3 text-slate-400" />
                    <span>{Math.round(eld.speed_mph)} mph</span>
                  </div>
                )}
                {typeof eld.odometer_miles === 'number' && (
                  <div className="flex items-center gap-1">
                    <Truck className="w-3 h-3 text-slate-400" />
                    <span>{Math.round(eld.odometer_miles).toLocaleString()} mi</span>
                  </div>
                )}
                {typeof eld.fuel_pct === 'number' && (
                  <div className="flex items-center gap-1">
                    <Fuel className="w-3 h-3 text-slate-400" />
                    <span>{Math.round(eld.fuel_pct)}% fuel</span>
                  </div>
                )}
                {eld.engine_state && (
                  <div className="flex items-center gap-1">
                    <Zap className="w-3 h-3 text-slate-400" />
                    <span className="capitalize">{eld.engine_state}</span>
                  </div>
                )}
              </div>
            )}
          />
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-3 italic">
        Both sources are always shown independently. ELD data never replaces phone GPS.
      </p>
    </div>
  );
};

export default DualLocationCard;
